import React, { useState, useEffect, useRef } from "react";
import PrincipalT from "./PrincipalT";
import "./EncabezadoSeccion.css";

// Bandera para activar/desactivar el encabezado editorial (con false todo queda exactamente como antes)
export const USAR_ENCABEZADO_EDITORIAL = true;

/**
 * Hook para detectar si la pantalla es Tablet o PC (>= 768px) sin saltos de layout
 */
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQuery = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  return matches;
};

/**
 * Componente EncabezadoSeccion
 * Estructura editorial reutilizable para los títulos de las secciones:
 * <header className="encabezado-editorial">
 *   <span className="encabezado-etiqueta">{etiqueta}</span> (solo si existe)
 *   <h2 className="encabezado-titulo">{titulo}</h2>
 * </header>
 *
 * - Tablet y PC (>= 768px): Encabezado editorial con h2 semántico.
 * - Celular (<= 767px): No se toca nada, renderiza PrincipalT o fallback original.
 * - Si USAR_ENCABEZADO_EDITORIAL = false: Renderiza exactamente como estaba antes.
 * - Tarea 3: Entrada suave una sola vez con IntersectionObserver (transform: scaleX y opacity).
 */
const EncabezadoSeccion = ({
  titulo,
  etiqueta,
  compacto = false,
  nivel = "h2",
  fallback = null,
}) => {
  const isTabletOrDesktop = useMediaQuery("(min-width: 768px)");
  const headerRef = useRef(null);

  // Detección inicial: si prefiere movimiento reducido o no hay IntersectionObserver, visible directo
  const [isVisible, setIsVisible] = useState(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return true;
    }
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return true;
    }
    return false;
  });

  // IntersectionObserver ejecutado una sola vez
  useEffect(() => {
    if (isVisible || !isTabletOrDesktop || !USAR_ENCABEZADO_EDITORIAL) return;
    const node = headerRef.current;
    if (!node) return;

    // Verificación inmediata si ya está dentro del viewport
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const rect = node.getBoundingClientRect();
    if (rect.top < windowHeight && rect.bottom > 0) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Una sola vez
        }
      },
      { threshold: 0.01, rootMargin: "0px 0px 40px 0px" }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [isVisible, isTabletOrDesktop]);

  // Con false todo queda EXACTAMENTE como ahora.
  // En celular (<= 767px) no se cambia nada.
  if (!USAR_ENCABEZADO_EDITORIAL || !isTabletOrDesktop) {
    if (fallback) return fallback;
    return <PrincipalT titulo={titulo} compacto={compacto} nivel={nivel} />;
  }

  return (
    <header
      ref={headerRef}
      className={`encabezado-editorial ${isVisible ? "encabezado-editorial--visible" : ""}`.trim()}
    >
      {etiqueta && <span className="encabezado-etiqueta">{etiqueta}</span>}
      <h2 className="encabezado-titulo">{titulo}</h2>
    </header>
  );
};

export default EncabezadoSeccion;
